import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FileText, Eye, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getCountryName } from '../../utils/countries';

const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
const buildQrUrl = (token) => `${PUBLIC_BASE_URL}/public/qr/${token}`;

const Section = ({ title, rows }) => {
  const filtered = rows.filter(([, v]) => v);
  if (filtered.length === 0) return null;
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        {filtered.map(([label, value], i) => (
          <div key={i} className="flex flex-col sm:flex-row">
            <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{label}:</span>
            <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const badge = (val) => {
  if (!val) return null;
  const isPos = ['Yes', 'Pass', 'Confirmed'].includes(val);
  const isNeg = ['No', 'Fail', 'Not confirmed'].includes(val);
  if (!isPos && !isNeg) return val;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded ${isPos ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{val}</span>;
};

export const FlashingPrintView = ({ product, project, user, showActions, onPreviewSubmittal, onDownloadSubmittal }) => {
  if (!product) return null;
  const fcc = product.fireCodeCompliance || {};
  const w = product.warranty || {};
  const vc = product.visualCoverage || {};
  const qrToken = product.qrToken;
  const imgUrl = product.imageCloudinary?.urls?.medium || product.imageUrl || null;

  return (
    <div className="p-4 sm:p-6 text-slate-800 bg-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4 pb-3 border-b">
        <div className="flex items-start gap-3">
          {imgUrl ? (
            <img src={imgUrl} alt={product.productNameModel} className="w-16 h-16 rounded-lg object-cover border border-slate-200 shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="h-6 w-6 text-slate-300" /></div>
          )}
          <div>
            <h2 className="text-base font-bold text-slate-900">{product.productNameModel}</h2>
            <p className="text-xs text-slate-500">{product.manufacturerName || ''}{product.countryCode ? ` | ${getCountryName(product.countryCode)}` : ''}</p>
            {project?.name && <p className="text-xs text-slate-400 mt-0.5">Project: {project.name}</p>}
          </div>
        </div>
        {qrToken && (
          <div className="shrink-0 flex flex-col items-center">
            <QRCodeSVG value={buildQrUrl(qrToken)} size={56} level="L" />
            <span className="text-[8px] text-slate-400 mt-1">Specard QR</span>
          </div>
        )}
      </div>

      <Section title="Product Identification" rows={[
        ['Country of Manufacturer', product.countryCode ? getCountryName(product.countryCode) : null],
        ['Product Role', Array.isArray(product.productRole) ? product.productRole.join(', ') : product.productRole],
        ['Application Area', Array.isArray(product.applicationArea) ? product.applicationArea.join(', ') : product.applicationArea],
      ]} />

      <Section title="Material & Fabrication Properties" rows={[
        ['Metal Type', product.metalType],
        ['Gauge / Thickness', product.gaugeThickness || product.gauge],
        ['Finish / Coating', product.finishCoating || product.materialPerformance?.coating || product.materialPerformance?.finishType],
        ['Fabrication Method', product.fabricationMethod || product.fabrication?.fabricationMethod],
      ]} />

      <Section title="Fire, Code & Assembly Compliance" rows={[
        ['NFPA 285 Compatibility', badge(fcc.nfpa285Compatibility)],
        ['ASTM E84', fcc.astmE84],
        ['Assembly Reference', fcc.assemblyReference],
        ['Local Code Notes', fcc.localCodeNotes],
      ]} />

      <Section title="Warranty & Responsibility" rows={[
        ['Manufacturer Material Warranty', w.manufacturerWarrantyYears ? `${w.manufacturerWarrantyYears} years` : null],
        ['Installer Workmanship Warranty', w.installerWarrantyYears ? `${w.installerWarrantyYears} years` : null],
        ['Scope Exclusions', w.scopeExclusions || w.exclusions],
        ['Maintenance Responsibility', w.maintenanceResponsibility || w.maintenanceRequirements],
      ]} />

      {/* Submittals */}
      {product.submittals && product.submittals.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals</h3>
          <div className="space-y-1">
            {product.submittals.map(s => (
              <div key={s.id} className="flex items-center gap-2 text-xs">
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="w-36 text-slate-500">{s.documentType}</span>
                <span className="flex-1 text-slate-700">{s.fileName}</span>
                <span className="text-slate-400">{s.bytes ? `${(s.bytes / 1024).toFixed(1)} KB` : 'Unknown'}</span>
                {showActions && (
                  <div className="flex items-center gap-1 print:hidden">
                    {onPreviewSubmittal && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onPreviewSubmittal(s)}><Eye className="h-3.5 w-3.5" /></Button>}
                    {onDownloadSubmittal && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onDownloadSubmittal(s.id, s.fileName)}><Download className="h-3.5 w-3.5" /></Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visual Coverage */}
      {vc.elevationUrl && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Visual Coverage</h3>
          <p className="text-[10px] text-slate-400 italic mb-2">Illustrative only — not part of specification</p>
          <div className="relative border rounded overflow-hidden">
            <img src={vc.elevationUrl} alt="Elevation" className="w-full" style={{ display: 'block' }} />
            {(vc.markers || []).map((m, i) => {
              const colors = { 'Sill': '#3b82f6', 'Head': '#ea580c', 'Parapet': '#16a34a' };
              const shapes = { 'Sill': 'rounded-sm' };
              const isSquare = shapes[m.type] === 'rounded-sm';
              return (
                <div key={i}
                  className={`absolute border-2 border-white shadow-lg ${isSquare ? 'w-3.5 h-3.5 rounded-sm' : 'w-3 h-3 rounded-full'}`}
                  style={{ left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%, -50%)', backgroundColor: `${colors[m.type] || '#666'}d9` }}
                  title={m.type} />
              );
            })}
          </div>
          {vc.markers?.length > 0 && (
            <p className="text-[10px] text-slate-500 mt-1">{vc.markers.length} marker(s) placed</p>
          )}
        </div>
      )}
    </div>
  );
};

export const QRThumbnail = ({ qrToken, size = 32 }) => {
  if (!qrToken) return <div style={{ width: size, height: size }} className="bg-slate-100 rounded" />;
  return <QRCodeSVG value={buildQrUrl(qrToken)} size={size} level="L" />;
};
